// Modified event-components/EventDetails.js with visibility, community info, owner display and description in header
import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faCalendarAlt,
    faClock,
    faMapMarkerAlt,
    faInfoCircle,
    faUser,
    faEye,
    faUsers,
    faTrash
} from '@fortawesome/free-solid-svg-icons';
import { useLanguage } from '../../../../contexts/LanguageContext';

function EventDetails({ eventData, theme = "dark", hideDateInDetails = false, canEdit = false, onDeleteEvent }) {
    const { t } = useLanguage();
    const formatDate = (dateStr) => {
        const date = new Date(dateStr);
        return date.toLocaleDateString('sl-SI', { 
            weekday: 'long', 
            year: 'numeric', 
            month: 'long', 
            day: 'numeric' 
        });
    };

    // Format time to remove seconds
    const formatTime = (timeStr) => {
        if (!timeStr) return '';
        
        // If the time already doesn't have seconds, return as is
        if (!timeStr.includes(':')) return timeStr;
        
        // Check if time has the format HH:MM:SS
        const timeParts = timeStr.split(':');
        if (timeParts.length === 3) {
            // Return only hours and minutes (HH:MM)
            return `${timeParts[0]}:${timeParts[1]}`;
        }
        
        // Return original time if it doesn't match expected format
        return timeStr;
    };

    // Format visibility for display
    const formatVisibility = (visibility) => {
        if (!visibility) return t('events.visibility.default');

        switch(visibility.toLowerCase()) {
            case 'public':
                return t('events.visibility.public');
            case 'private':
                return t('events.visibility.private');
            case 'community':
                return t('events.visibility.community');
            default:
                return visibility;
        }
    };

    // Handle delete button click
    const handleDeleteClick = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (onDeleteEvent) {
            onDeleteEvent(eventData.id);
        }
    };

    // Determine text class based on theme
    const textClass = theme === "light" ? "text-white" : "text-dark";
    const iconClass = theme === "light" ? "text-white-50" : "text-muted";
    
    // Check if we're on mobile
    const isMobile = window.innerWidth < 992;

    // For header layout, use a more compact horizontal display
    if (theme === "light") {
        return (
            <div className="d-flex flex-column w-100">
                {/* Top row with basic details */}
                <div className="d-flex flex-wrap gap-3 w-100 mb-1">
                    {!hideDateInDetails && (
                        <div className="d-flex align-items-center">
                            <FontAwesomeIcon icon={faCalendarAlt} className={iconClass + " me-2"} />
                            <span className={textClass}>{formatDate(eventData.date)}</span>
                        </div>
                    )}
                    <div className="d-flex align-items-center">
                        <FontAwesomeIcon icon={faClock} className={iconClass + " me-2"} />
                        <span className={textClass}>{formatTime(eventData.time)}</span>
                    </div>
                    <div className="d-flex align-items-center">
                        <FontAwesomeIcon icon={faMapMarkerAlt} className={iconClass + " me-2"} />
                        <span className={textClass}>{eventData.location}</span>
                    </div>
                    
                    {eventData.owner_display_name && (
                        <div className="d-flex align-items-center">
                            <FontAwesomeIcon icon={faUser} className={iconClass + " me-2"} />
                            <span className={textClass}>{t('events.details.createdBy', { username: eventData.owner_display_name })}</span>
                        </div>
                    )}
                    {/* Delete button - Only show if canEdit is true */}
                    {canEdit && (
                        <div className="d-flex align-items-center ms-auto">
                            <button
                                className={`btn btn-link ${iconClass} p-0`}
                                onClick={handleDeleteClick}
                                title={t('events.actions.deleteEvent')}
                            >
                                <FontAwesomeIcon icon={faTrash} />
                            </button>
                        </div>
                    )}
                </div>
                
                {/* Description in header - only if it exists */}
                {eventData.description && (
                    <div className="d-flex align-items-start mt-1">
                        <FontAwesomeIcon icon={faInfoCircle} className={iconClass + " me-2 mt-1"} />
                        <span className={textClass} style={{ 
                            fontSize: isMobile ? '0.85rem' : '0.9rem',
                            opacity: 0.9,
                            maxHeight: isMobile ? '60px' : '80px',
                            overflow: 'auto'
                        }}>
                            {eventData.description}
                        </span>
                    </div>
                )}
            </div>
        );
    }

    // For regular display, use the vertical layout
    return (
        <div className="d-flex flex-column gap-2">
            <div className="d-flex justify-content-between align-items-start">
                <div className="flex-grow-1">
                    {!hideDateInDetails && (
                        <div className="d-flex align-items-center">
                            <FontAwesomeIcon icon={faCalendarAlt} className="text-muted me-2" />
                            <span>{formatDate(eventData.date)}</span>
                        </div>
                    )}
                </div>
                {/* Delete button - Only show if canEdit is true */}
                {canEdit && (
                    <button
                        className="btn btn-link text-muted p-0"
                        onClick={handleDeleteClick}
                        title={t('events.actions.deleteEvent')}
                    >
                        <FontAwesomeIcon icon={faTrash} />
                    </button>
                )}
            </div>
            <div className="d-flex align-items-center">
                <FontAwesomeIcon icon={faClock} className="text-muted me-2" />
                <span>{formatTime(eventData.time)}</span>
            </div>
            <div className="d-flex align-items-center">
                <FontAwesomeIcon icon={faMapMarkerAlt} className="text-muted me-2" />
                <span>{eventData.location}</span>
            </div>
            {eventData.visibility && (
                <div className="d-flex align-items-center">
                    <FontAwesomeIcon icon={faEye} className="text-muted me-2" />
                    <span>{t('events.details.visibility')}: {formatVisibility(eventData.visibility)}</span>
                </div>
            )}
            {eventData.community_name && (
                <div className="d-flex align-items-center">
                    <FontAwesomeIcon icon={faUsers} className="text-muted me-2" />
                    <span>{eventData.community_name}</span>
                </div>
            )}
            {eventData.owner_display_name && (
                <div className="d-flex align-items-center">
                    <FontAwesomeIcon icon={faUser} className="text-muted me-2" />
                    <span>{t('events.details.createdBy', { username: eventData.owner_display_name })}</span>
                </div>
            )}
            {eventData.description && (
                <div className="d-flex align-items-start">
                    <FontAwesomeIcon icon={faInfoCircle} className="text-muted me-2 mt-1" />
                    <span>{eventData.description}</span>
                </div>
            )}
        </div>
    );
}

export default EventDetails;